import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  ViewChild,
  ViewContainerRef
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { IToggleButton, ToggleButtonComponent } from "@zaeper/angular-dashboard-view-lib";
import { LiveViewContainerComponent } from "../../components/live-view-container/live-view-container.component";
import { PreviewContainerComponent } from "../../components/preview-container/preview-container.component";
import { sessionStateServiceInjectionToken } from "../../global-states/session/session-state-service-injection-token";
import { ISessionStateService } from "../../global-states/session/i-session-state.service";
import { IReceiver } from "../../models/i-receiver";
import { filter } from "rxjs/operators";
import { DynamicDialogRef } from "primeng/dynamicdialog";
import { ButtonModule } from "primeng/button";

enum EReceiverViewMode {
  PREVIEW = 0,
  LIVE_VIEW = 1
}

@Component({
  selector: "app-fullscreen-preview-modal",
  templateUrl: "fullscreen-preview-modal.component.html",
  standalone: true,
  imports: [
    CommonModule,
    ToggleButtonComponent,
    LiveViewContainerComponent,
    PreviewContainerComponent,
    ButtonModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FullscreenPreviewModalComponent implements AfterViewInit, OnDestroy {
  public readonly receiverViewMode$: Observable<EReceiverViewMode>;
  public readonly eReceiverViewMode: typeof EReceiverViewMode = EReceiverViewMode;
  public readonly toggleButtons: IToggleButton[];
  public aspectRatio: string = "16/9";
  public receiverName: string = "";

  private readonly _subscriptions: Subscription = new Subscription();
  private readonly _receiverViewMode$: BehaviorSubject<EReceiverViewMode>;

  @ViewChild("previewerFrame", { read: ElementRef }) private readonly _previewerFrame!: ElementRef;
  @ViewChild("previewerFrameWrapper", { read: ElementRef }) private readonly _previewerFrameWrapper!: ElementRef;
  @ViewChild("previewContainer", { read: ViewContainerRef }) private readonly _previewContainer!: ViewContainerRef;
  @ViewChild("liveViewContainer", { read: ViewContainerRef }) private readonly _liveViewContainer!: ViewContainerRef;

  constructor(
    @Inject(sessionStateServiceInjectionToken) private readonly _sessionStateService: ISessionStateService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _dialogRef: DynamicDialogRef
  ) {
    this._receiverViewMode$ = new BehaviorSubject<EReceiverViewMode>(EReceiverViewMode.PREVIEW);
    this.receiverViewMode$ = this._receiverViewMode$.asObservable();
    this.toggleButtons = [
      {
        label: "Vorschau",
        iconName: "web_asset"
      },
      {
        label: "Live-Ansicht",
        iconName: "radio_button_checked"
      }
    ];

    const receiver: IReceiver | null = this._sessionStateService.getActiveReceiver();
    if (receiver?.resolutionX && receiver?.resolutionY) {
      this.aspectRatio = `${receiver.resolutionX}/${receiver.resolutionY}`;
    }
    this.receiverName = receiver?.name ?? "";
  }

  ngAfterViewInit(): void {
    this._handleReceiverViewModeChange();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  public switchViewMode(activeIndex: number): void {
    this._receiverViewMode$.next(activeIndex);
  }

  public close(): void {
    this._dialogRef.close();
  }

  private _handleReceiverViewModeChange(): void {
    this._subscriptions.add(
      this._receiverViewMode$.pipe(filter(() => this._sessionStateService.getActiveReceiver() !== null)).subscribe((receiverViewMode: EReceiverViewMode) => {
        this._changeDetectorRef.detectChanges();
        if (receiverViewMode === EReceiverViewMode.PREVIEW && this._previewContainer.length === 0) {
          this._previewContainer.createComponent(PreviewContainerComponent);
        }
        if (receiverViewMode === EReceiverViewMode.LIVE_VIEW && this._liveViewContainer.length === 0) {
          this._liveViewContainer.createComponent(LiveViewContainerComponent);
        }
        this._changeDetectorRef.detectChanges();
      })
    );
  }
}
